import React, { createContext, useContext, useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useClient, PaginatedResponse } from '@/stores/useClient.ts'
import { ProfPortfolio } from '@/models/profPortfolio.ts'

export interface PortfoliosQueryParams {
  page: number
  page_size: number
  search?: string
  o?: string
}

interface PortfoliosContextType {
  portfolios: ProfPortfolio[]
  totalCount: number
  isLoading: boolean
  error: Error | null
  params: PortfoliosQueryParams
  setPage: (page: number) => void
  setSort: (sort: string) => void
  setSearch: (search: string) => void
  refetch: () => void
}

const PortfoliosContext = createContext<PortfoliosContextType | null>(null)

export function usePortfoliosContext() {
  return useContext(PortfoliosContext)
}

export function PortfoliosProvider({ children }: { children: React.ReactNode }) {
  const { getPortfolios } = useClient()
  const queryClient = useQueryClient()

  const [params, setParams] = useState<PortfoliosQueryParams>({
    page: 1,
    page_size: 20,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['portfolios', params],
    queryFn: async () => {
      const res = await getPortfolios({
        page: params.page,
        page_size: params.page_size,
        search: params.search,
        o: params.o,
      })
      // API may return a plain array or a paginated response
      const raw = res.data
      if (Array.isArray(raw)) {
        return { results: raw as ProfPortfolio[], count: raw.length }
      }
      return raw as PaginatedResponse<ProfPortfolio>
    },
  })

  const setPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }))
  }, [])

  const setSort = useCallback((sort: string) => {
    setParams(prev => ({ ...prev, o: sort || undefined, page: 1 }))
  }, [])

  const setSearch = useCallback((search: string) => {
    setParams(prev => ({ ...prev, search: search || undefined, page: 1 }))
  }, [])

  const handleRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['portfolios'] })
  }, [queryClient])

  return (
    <PortfoliosContext.Provider value={{
      portfolios: data?.results ?? [],
      totalCount: data?.count ?? 0,
      isLoading,
      error: error as Error | null,
      params,
      setPage,
      setSort,
      setSearch,
      refetch: handleRefetch,
    }}>
      {children}
    </PortfoliosContext.Provider>
  )
}
